import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';

import _ from 'lodash';
import dayjs from 'dayjs';
import axios from 'axios';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { grey, green, red, amber, blue } from '@mui/material/colors';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShip, faPlane, faTruck, faUser, faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons';
import { faLock } from '@fortawesome/pro-light-svg-icons';

import Header from '../../Header';
import { calcOfferSum } from '../../utils/opportunity.js';
import { getTotalRates } from '../utils/rates.js';
import { statusToText } from '../utils/opportunity.js';
import CurrencyToText from '../../CurrencyToText.js';

import BookingsKanban from './BookingsPage.kanban.js';

const BOOKING_STAGES = ['Quote', 'Booking', 'Voyage', 'Invoicing', 'Closed'];

function BookingsPage() {
    const { org, opportunities, accounts, accesstoken, me, exchangeRates, onUpdateOpportunity } = useOutletContext();
    const navigate = useNavigate();

    const bookingOpportunities = useMemo(() => {
        if (!opportunities) return [];
        return opportunities.filter(o => {
            if (o.status === 'deleted') return false;
            return (o.tags || []).some(t => t.label?.startsWith('Booking:'));
        });
    }, [opportunities]);

    function onOpen({ opportunity }) {
        navigate(`../opportunities/${opportunity.id}`);
    }

    async function onUpdateBookingStage({ opportunity, stage }) {
        const newTags = [
            ...(opportunity.tags || []).filter(t => !t.label?.startsWith('Booking:')),
            { label: `Booking: ${stage}` }
        ];
        // Optimistic update
        onUpdateOpportunity({ opportunity: { ...opportunity, tags: newTags } });
        try {
            await axios.put(
                `/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${org.id}`,
                { opportunity: { tags: newTags } }
            );
        } catch (e) {
            console.error('[Bookings] Error updating stage:', e);
            // Revert on error
            onUpdateOpportunity({ opportunity });
        }
    }

    if (!opportunities || !accounts) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '64px' }}>
                <Typography variant='caption' color='text.secondary'>Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Header
                title='Bookings'
                buttons={[]}
            />
            <Box sx={{ p: '12px' }}>
                <Paper sx={{ p: '12px', border: `1px solid ${grey[300]}`, mb: '12px' }} elevation={0}>
                    <Typography variant='body2' sx={{ fontWeight: 800, mb: '4px' }}>Bookings</Typography>
                    <Typography variant='caption' sx={{ fontWeight: 500 }}>
                        <b>{bookingOpportunities.length}</b> {bookingOpportunities.length === 1 ? 'booking' : 'bookings'}
                    </Typography>
                </Paper>
                <BookingsKanban
                    opportunities={bookingOpportunities}
                    accounts={accounts}
                    onOpen={onOpen}
                    onUpdateStage={onUpdateBookingStage}
                    org={org}
                    exchangeRates={exchangeRates}
                    stages={BOOKING_STAGES}
                />
            </Box>
        </Box>
    );
}

export default BookingsPage;
